import { faCheck, faFile, faPaperclip } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, DatePicker, Form, Input, Modal, Select, Space, Typography } from 'antd'
import dayjs from 'dayjs'
import { Attachment } from '../../components/attachments/attachmentsModel'
import ReceiptCatalogBrowser from '../../components/Items/ReceiptCatalogBrowser'
import ReceiptItems from '../../components/Items/Receipts/ReceiptItems'
import { VendorItem } from '../../components/Vendors/vendorsModel'
import { LegacyReceiptData } from '../../domain/jobs/jobs'
import { isReceiptComplete } from './receiptsModel'
import './ReceiptModal.css'

type Props = {
  open: boolean
  receipt: LegacyReceiptData | undefined
  vendors: VendorItem[]
  attachments: Attachment[]
  isSaving: boolean
  onChange: (changes: Partial<LegacyReceiptData>) => void
  onUploadAttachments: () => void
  onSave: () => void
  onCancel: () => void
}

const ReceiptModal = ({
  open,
  receipt,
  vendors,
  attachments,
  isSaving,
  onChange,
  onUploadAttachments,
  onSave,
  onCancel
}: Props) => {
  if (!receipt) {
    return null
  }

  const canUploadAttachments = !!receipt.vendorId && !!receipt.receiptNumber

  return <Modal
    title={`${receipt.vendorName || 'New'} Receipt`}
    open={open}
    onCancel={onCancel}
    width={1100}
    footer={<Space>
      <Button
        type="primary"
        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
        icon={<FontAwesomeIcon icon={faPaperclip} />}
        disabled={!canUploadAttachments}
        onClick={onUploadAttachments}
      >
        Upload Receipt Attachments
      </Button>
      <Button
        type="primary"
        icon={<FontAwesomeIcon icon={faCheck} />}
        disabled={!isReceiptComplete(receipt)}
        loading={isSaving}
        onClick={onSave}
      >
        Update Receipt
      </Button>
    </Space>}
  >
    <div className="receipt-modal__body">
      <ReceiptCatalogBrowser onTemplateAdd={() => { }} />
      <div className="receipt-modal__form">
        <Form layout="vertical">
          <div className="receipt-modal__fields">
            <Form.Item label="Vendor" required>
              <div data-testid="receipt-vendor">
                <Select
                  value={receipt.vendorId || undefined}
                  onChange={vendorId => onChange({
                    vendorId,
                    vendorName: vendors.find(x => x.id === vendorId)?.name ?? ''
                  })}
                  options={vendors.map(vendor => ({ label: vendor.name, value: vendor.id }))}
                  showSearch
                  optionFilterProp="label"
                />
              </div>
            </Form.Item>
            <Form.Item label="Receipt Date" required>
              <DatePicker
                style={{ width: '100%' }}
                value={receipt.receiptDate ? dayjs(receipt.receiptDate) : null}
                onChange={date => onChange({ receiptDate: date ? date.format('MM/DD/YYYY') : '' })}
              />
            </Form.Item>
            <Form.Item label="Receipt #" required>
              <Input
                maxLength={20}
                value={receipt.receiptNumber}
                onChange={e => onChange({ receiptNumber: e.target.value })}
              />
            </Form.Item>
          </div>

          <Form.Item label={`Attachments (${attachments.length})`}>
            <div className="receipt-modal__attachments">
              {attachments.length === 0 && <Typography.Text type="secondary">
                Click the green <b>Upload Receipt Attachments</b> button in the footer below to create
                attachments for this receipt.
              </Typography.Text>}
              {attachments.map(attachment => <a
                key={attachment.id}
                className="receipt-modal__attachment"
                href={attachment.url}
                target="_blank"
                rel="noreferrer"
              >
                {attachment.thumbnail
                  ? <img src={attachment.thumbnail} alt={attachment.name} />
                  : <FontAwesomeIcon icon={faFile} size="2x" />}
                <span>{attachment.name}</span>
              </a>)}
            </div>
          </Form.Item>

          <ReceiptItems
            receiptId={receipt.id}
            customTotal={receipt.customTotal ?? undefined}
            onCustomTotalChanged={customTotal => onChange({ customTotal: customTotal ?? null })}
          />

          <Form.Item label="Notes">
            <Input.TextArea
              rows={3}
              value={receipt.notes ?? ''}
              onChange={e => onChange({ notes: e.target.value })}
            />
          </Form.Item>
        </Form>
      </div>
    </div>
  </Modal>
}

export default ReceiptModal
